// LIVRAISON_1_8_1_V3_APPLIED @ 20260716_195858
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from '../hooks/useApi';
import { 
  FileText, 
  Plus,
  ChevronUp,
  ChevronDown,
  Trash2,
  Camera,
  Save,
  Download,
  Clock,
  MapPin,
  Package,
  Truck as TruckIcon,
  Edit2,
  Check,
  X,
  Image,
  FileImage,
  Building2,
  Navigation,
  Crosshair,
  Share2,
  Mail,
  MessageSquare,
  Printer,
  Eye,
  User,
  Phone,
  Scan,
  Pen, ArrowLeft
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { NativeSelect, NativeSelectOption } from '../components/ui/native-select';
import { roundToQuarterHour, formatHoursDecimal, formatHoursHuman } from '../lib/hoursFormat';
import { formatStopDateTime, formatShortDate } from '../lib/dateFormat';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { StatusBadge } from '../components/StatusBadge';
import { PrintPreviewModal } from '../components/PrintPreviewModal';
import { SignaturePad } from '../components/SignaturePad';
import { getTodayDate, getCurrentTime, formatDuration } from '../lib/utils';
import { toast } from 'sonner';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';

// Timeout helper — évite qu'une promesse hang indéfiniment
// (bug historique du "End Trip" qui gèle sur Android en zone lente).
const withTimeout = (promise, ms, label = 'operation') =>
  Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error(`${label} timeout après ${ms}ms`)), ms)
    ),
  ]);

// Convertit un Blob en base64 pur (sans le préfixe data:) pour Capacitor Filesystem.
const blobToBase64 = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onloadend = () => {
    const result = reader.result || '';
    const b64 = typeof result === 'string' ? result.split(',')[1] : '';
    resolve(b64);
  };
  reader.onerror = reject;
  reader.readAsDataURL(blob);
});

export const DailyReportPage = () => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { get, post, patch, del } = useApi();
  
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);
  const [editingStop, setEditingStop] = useState(null);
  const [showStopDialog, setShowStopDialog] = useState(false);
  const [savingStop, setSavingStop] = useState(false);   // Anti double-clic + feedback visuel
  const [endingDay, setEndingDay] = useState(false);     // Anti-freeze bouton "Fin de trajet"
  const [showCompanyDialog, setShowCompanyDialog] = useState(false);
  const [showScanDialog, setShowScanDialog] = useState(false);
  const [selectedStopId, setSelectedStopId] = useState(null);
  const [signatureModalStop, setSignatureModalStop] = useState(null);  // {id, ...} or null
  const [companyInfo, setCompanyInfo] = useState({
    logo_base64: null,
    name: '',
    address: '',
    phone: '',
    email: '',
    custom_text: ''
  });
  
  const [newStop, setNewStop] = useState({
    client_name: '',
    address: '',
    delivery_note_numbers: [],
    quantities: { palettes: 0, units: 0, packages: 0, weight_kg: null },
    stop_type: 'delivery',
    equipment_used: 'none',
    arrival_datetime: '',
    departure_datetime: '',
    notes: ''
  });
  
  const [deliveryNoteInput, setDeliveryNoteInput] = useState('');
  const [pendingBolFiles, setPendingBolFiles] = useState([]);
  const [lastBolExtraction, setLastBolExtraction] = useState(null);
  const fileInputRef = useRef(null);
  const logoInputRef = useRef(null);
  const autoSaveTimeoutRef = useRef(null);
  const [gpsTracking, setGpsTracking] = useState({ totalKm: 0, lastPosition: null });
  const [locatingAddress, setLocatingAddress] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [shareEmail, setShareEmail] = useState('');
  const [sharePhone, setSharePhone] = useState('');
  const [loadedDocuments, setLoadedDocuments] = useState({}); // { stopId: [{ id, image_url, ... }] }

  // Load documents for a single stop (used after new upload)
  const loadDocumentsForStop = useCallback(async (reportId, stopId) => {
    if (!reportId || !stopId) return;
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/daily-reports/${reportId}/stops/${stopId}/documents`,
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
      );
      if (response.ok) {
        const docs = await response.json();
        setLoadedDocuments(prev => ({ ...prev, [stopId]: docs }));
      }
    } catch (err) {
      console.error('Error loading documents for stop', stopId, err);
    }
  }, []);

  // Load scanned documents for all stops (URLs only, not base64 — lightweight)
  const loadStopDocuments = useCallback(async () => {
    if (!report || !report.stops) return;
    
    const docsMap = {};
    for (const stop of report.stops) {
      if (stop.scanned_documents && stop.scanned_documents.length > 0) {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/daily-reports/${report.id}/stops/${stop.id}/documents`,
            { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
          );
          if (response.ok) {
            docsMap[stop.id] = await response.json();
          }
        } catch (err) {
          console.error('Error loading documents for stop', stop.id, err);
        }
      }
    }
    setLoadedDocuments(docsMap);
  }, [report]);

  useEffect(() => {
    loadStopDocuments();
  }, [loadStopDocuments]);

  const [loadingDocs, setLoadingDocs] = useState(false);
  const [clockTick, setClockTick] = useState(0); // Triggers re-render for live hours calculation

  // Timer to update hours in real-time while trip is in progress
  useEffect(() => {
    if (report && report.start_time && !report.end_time) {
      const interval = setInterval(() => {
        setClockTick(prev => prev + 1);
      }, 60000); // Update every minute
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report?.start_time, report?.end_time]);

  // Get current time in HH:MM format
  const getCurrentTimeFormatted = () => {
    const now = new Date();
    return now.toTimeString().slice(0, 5); // "HH:MM"
  };

  // Get current datetime in datetime-local format (LOCAL time, not UTC)
  const getCurrentDateTimeFormatted = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Reverse geocode coordinates to address
  const getAddressFromCoords = async (lat, lng) => {
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`
      );
      const data = await response.json();
      if (data.results && data.results[0]) {
        return data.results[0].formatted_address;
      }
      return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
    } catch (err) {
      console.error('Geocoding error:', err);
      return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
    }
  };

  // Get current location and address
  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Géolocalisation non supportée'));
        return;
      }
      if (window.__IS_NATIVE__ === true && localStorage.getItem('propilote_location_consent') !== 'accepted') {
        reject(new Error('Localisation non autorisée'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          const address = await getAddressFromCoords(latitude, longitude);
          resolve({ lat: latitude, lng: longitude, address });
        },
        (error) => {
          reject(error);
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    });
  };

  // Fetch or create report
  useEffect(() => {
    if (reportId && reportId !== 'new') {
      fetchReport();
    } else {
      createNewReport();
    }
    fetchCompanySettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId]);

  // Auto-save effect
  useEffect(() => {
    if (!report || !autoSaveEnabled) return;
    
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    
    autoSaveTimeoutRef.current = setTimeout(() => {
      saveReport(true);
    }, 3000);
    
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report?.start_time, report?.end_time, report?.end_date, report?.total_km, report?.subcontractor]);

  const fetchReport = async () => {
    try {
      const result = await get(`/daily-reports/${reportId}`);
      if (result.success) {
        setReport(result.data);
        if (result.data.company_info) {
          setCompanyInfo(result.data.company_info);
        }
        // Pre-load documents for completed reports (for printing)
        if (result.data.status === 'completed') {
          loadAllDocumentsForReport(result.data);
        }
        // Restauration automatique du brouillon d'arret non sauvegarde
        // (si crash reseau/app pendant un ajout precedent)
        try {
          const draftRaw = localStorage.getItem(`propilote_draft_stop_${result.data.id}`);
          if (draftRaw) {
            const draft = JSON.parse(draftRaw);
            const draftAgeHours = (Date.now() - draft.timestamp) / (1000 * 60 * 60);
            // Restauration seulement si brouillon < 24h ET rapport pas complete
            if (draftAgeHours < 24 && draft.newStop?.client_name && result.data.status !== 'completed') {
              setNewStop(draft.newStop);
              setShowStopDialog(true);
              toast.info('Brouillon d\'arret restaure. Verifiez les infos et cliquez sur Ajouter.', {
                duration: 8000
              });
            } else {
              // Brouillon trop vieux ou rapport complete : on nettoie
              localStorage.removeItem(`propilote_draft_stop_${result.data.id}`);
            }
          }
        } catch (e) { /* localStorage indisponible, non bloquant */ }
      } else {
        toast.error('Rapport non trouvé');
        navigate('/reports');
      }
    } catch (err) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };
  
  // Load documents for a specific report
  const loadAllDocumentsForReport = async (reportData) => {
    if (!reportData || !reportData.stops || reportData.stops.length === 0) return;
    
    const docs = {};
    
    for (const stop of reportData.stops) {
      if (stop.scanned_documents && stop.scanned_documents.length > 0) {
        try {
          const result = await get(`/daily-reports/${reportData.id}/stops/${stop.id}/documents`);
          if (result.success && result.data) {
            docs[stop.id] = result.data;
          }
        } catch (err) {
          console.error(`Error loading documents for stop ${stop.id}:`, err);
        }
      }
    }
    
    setLoadedDocuments(docs);
  };

  const createNewReport = async () => {
    try {
      // Auto-fill with current time
      const currentTime = getCurrentTimeFormatted();
      const result = await post('/daily-reports', {
        date: getTodayDate(),
        start_time: currentTime,
        company_info: companyInfo
      });
      if (result.success) {
        setReport(result.data);
        navigate(`/reports/${result.data.id}`, { replace: true });
        toast.success('Nouveau rapport créé');

        // Auto-import dispatch stop if passed via navigation state
        const dispatchStop = location.state?.dispatchStop;
        if (dispatchStop && dispatchStop.id) {
          try {
            const addResult = await post(`/dispatch/driver/stops/${dispatchStop.id}/add-to-report`, {
              report_id: result.data.id,
            });
            if (addResult.success) {
              toast.success(`Arrêt "${dispatchStop.client_name}" ajouté au rapport`);
              // Refresh report to show the added stop
              const refreshed = await get(`/daily-reports/${result.data.id}`);
              if (refreshed.success) setReport(refreshed.data);
            }
          } catch (err) {
            console.error('Error adding dispatch stop to report:', err);
          }
        }
      }
    } catch (err) {
      toast.error('Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanySettings = async () => {
    try {
      const result = await get('/settings/company');
      if (result.success && result.data) {
        setCompanyInfo(prev => ({ ...prev, ...result.data }));
      }
    } catch (err) {
      console.error('Error fetching company settings:', err);
    }
  };

  // Load documents when opening share dialog (uses current report state)
  const handleOpenShareDialog = async () => {
    setShowShareDialog(true);
    if (report && Object.keys(loadedDocuments).length === 0) {
      setLoadingDocs(true);
      await loadAllDocumentsForReport(report);
      setLoadingDocs(false);
    }
  };

  const saveReport = async (isAutoSave = false) => {
    if (!report) return;
    
    if (!isAutoSave) setSaving(true);
    
    try {
      // Calculate total hours if both times are set
      let totalHours = report.total_hours;
      if (report.start_time && report.end_time) {
        const startDate = report.date;
        const endDate = report.end_date || report.date;
        const start = new Date(`${startDate}T${report.start_time}`);
        const end = new Date(`${endDate}T${report.end_time}`);
        totalHours = (end - start) / (1000 * 60 * 60);
        if (totalHours < 0) totalHours = 0;
      }
      
      // Calculate total km if both values are set
      let totalKm = report.total_km;
      if (report.start_km && report.end_km) {
        totalKm = report.end_km - report.start_km;
      }
      
      const result = await patch(`/daily-reports/${report.id}`, {
        subcontractor: report.subcontractor,
        company_info: companyInfo,
        date: report.date,
        start_time: report.start_time,
        end_time: report.end_time,
        end_date: report.end_date || null,
        total_hours: totalHours,
        start_km: report.start_km,
        end_km: report.end_km,
        total_km: totalKm
      });
      
      if (result.success) {
        setReport(result.data);
        if (!isAutoSave) toast.success('Rapport sauvegardé');
      }
    } catch (err) {
      if (!isAutoSave) toast.error('Erreur lors de la sauvegarde');
    } finally {
      if (!isAutoSave) setSaving(false);
    }
  };

  const saveCompanySettings = async () => {
    try {
      await post('/settings/company', companyInfo);
      toast.success('Paramètres entreprise sauvegardés');
      setShowCompanyDialog(false);
    } catch (err) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setCompanyInfo(prev => ({ ...prev, logo_base64: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const addStop = async (insertAt = null) => {
    if (!newStop.client_name || !newStop.address) {
      toast.error('Veuillez remplir le nom du client et l\'adresse');
      return;
    }

    // Anti double-clic : bloque si un enregistrement est deja en cours
    if (savingStop) {
      console.warn('[addStop] Enregistrement deja en cours, clic ignore');
      return;
    }

    // Sauvegarde locale AVANT l'appel API (fallback si crash reseau/app)
    // Si l'utilisateur perd la connexion, ces donnees seront preservees
    const draftKey = `propilote_draft_stop_${report.id}`;
    try {
      localStorage.setItem(draftKey, JSON.stringify({
        newStop,
        pendingBolFiles: pendingBolFiles.length,   // count only, files trop lourds
        timestamp: Date.now()
      }));
    } catch (e) { /* localStorage plein, non bloquant */ }

    setSavingStop(true);

    try {
      let url = `/daily-reports/${report.id}/stops`;
      if (insertAt !== null) {
        url += `?insert_at=${insertAt}`;
      }
      
      const result = await post(url.split('?')[0], newStop, {
        params: insertAt !== null ? { insert_at: insertAt } : undefined
      });
      
      if (result.success) {
        // Nettoie le brouillon localStorage (succes)
        try { localStorage.removeItem(`propilote_draft_stop_${report.id}`); } catch (e) {}
        
        // CAS OFFLINE : la mutation a ete mise en file d'attente IndexedDB
        // Le backend n'a PAS repondu, donc result.data n'est PAS un rapport complet.
        // On ajoute l'arret localement au rapport existant SANS ecraser le rapport.
        if (result.queued || result.offline) {
          const localStop = {
            id: `local-stop-${Date.now()}`,
            _local: true,
            _pending_sync: true,
            ...newStop,
          };
          setReport(prev => ({
            ...prev,
            stops: [...(prev.stops || []), localStop]
          }));
          resetNewStop();
          setShowStopDialog(false);
          toast.success('Arret enregistre hors ligne. Il sera synchronise au retour du signal.', {
            duration: 5000
          });
          return;   // sortie precoce, pas de traitement online (BOL upload, dispatch sync)
        }
        
        // Auto-sync end_time if new stop has a later time
        const updatedReport = result.data;
        const latestStopTime = getLatestStopTime(updatedReport.stops || []);
        if (latestStopTime && updatedReport.start_time) {
          const reportEndDate = updatedReport.end_date || updatedReport.date;
          const currentEnd = updatedReport.end_time ? new Date(`${reportEndDate}T${updatedReport.end_time}`) : null;
          
          if (!currentEnd || latestStopTime > currentEnd) {
            const newEndTime = `${String(latestStopTime.getHours()).padStart(2,'0')}:${String(latestStopTime.getMinutes()).padStart(2,'0')}`;
            const endPatch = { end_time: newEndTime };
            const syncResult = await patch(`/daily-reports/${updatedReport.id}`, endPatch);
            if (syncResult.success) {
              setReport(syncResult.data);
            } else {
              setReport(updatedReport);
            }
          } else {
            setReport(updatedReport);
          }
        } else {
          setReport(updatedReport);
        }

        // Save references before reset clears them
        const bolFilesToUpload = [...pendingBolFiles];
        const savedClientName = newStop.client_name;
        const previousStopIds = (report.stops || []).map(s => s.id);
        
        // Find the newly added stop (ID not in previous list)
        const addedStop = (updatedReport.stops || []).find(s => !previousStopIds.includes(s.id));
        
        // Save Few-Shot correction if scan was used and fields were modified
        const savedExtraction = lastBolExtraction;
        
        resetNewStop();
        setShowStopDialog(false);
        toast.success('Arret ajoute');

        // Send correction for autolearning (background, non-blocking)
        if (savedExtraction && addedStop) {
          try {
            const corrected = {
              client_name: savedClientName,
              city: newStop.city || '',
              address: newStop.address || '',
              expeditor_name: newStop.expeditor_name || '',
              expeditor_city: newStop.expeditor_city || '',
              bol_number: (newStop.delivery_note_numbers || [])[0] || '',
              palettes: newStop.quantities?.palettes || 0,
              weight_kg: newStop.quantities?.weight_kg || 0,
            };
            fetch(`${process.env.REACT_APP_BACKEND_URL}/api/scan/corrections`, {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
              body: JSON.stringify({ scan_type: 'bol', original_extraction: savedExtraction, corrected_data: corrected }),
            }).catch(() => {});
          } catch (e) { /* non-blocking */ }
        }

        // Upload pending BOL scanned files to the new stop
        if (addedStop && bolFilesToUpload.length > 0) {
          for (const bolFile of bolFilesToUpload) {
            try {
              const uploadForm = new FormData();
              uploadForm.append('file', bolFile.blob, bolFile.name);
              uploadForm.append('enhance_mode', 'auto');
              const uploadRes = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/daily-reports/${updatedReport.id}/stops/${addedStop.id}/documents`,
                {
                  method: 'POST',
                  headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                  body: uploadForm
                }
              );
              if (!uploadRes.ok) {
                console.error('BOL upload failed:', await uploadRes.text());
              }
            } catch (uploadErr) {
              console.error('BOL upload error:', uploadErr);
            }
          }
          toast.success(`${bolFilesToUpload.length} BOL joint(s) a l'arret`);
          // Reload report to show uploaded documents
          try {
            const refreshResult = await get(`/daily-reports/${updatedReport.id}`);
            if (refreshResult.success) setReport(refreshResult.data);
          } catch (e) { /* ignore refresh error */ }
        }

        // Bi-directional sync: create dispatch stop for manually added stops
        if (addedStop && !addedStop.dispatch_stop_id) {
          try {
            const syncData = {
              report_id: updatedReport.id,
              client_name: addedStop.client_name || '',
              address: addedStop.address || '',
              stop_type: addedStop.stop_type || 'livraison',
              arrival_datetime: addedStop.arrival_datetime || null,
              departure_datetime: addedStop.departure_datetime || null,
              required_date: updatedReport.date,
              required_time: addedStop.arrival_datetime ? addedStop.arrival_datetime.split('T')[1]?.substring(0,5) : '',
              notes: addedStop.notes || '',
            };
            const dispatchResult = await post('/dispatch/driver/stops/from-report', syncData);
            if (dispatchResult.success && dispatchResult.data?.dispatch_stop_id) {
              await patch(`/daily-reports/${updatedReport.id}/stops/${addedStop.id}`, {
                dispatch_stop_id: dispatchResult.data.dispatch_stop_id
              });
            }
          } catch (syncErr) {
            console.error('Dispatch sync for manual stop:', syncErr);
          }
        }
      } else {
        // result.success === false : le hook useApi a intercepte une erreur
        // On garde les donnees dans newStop (pas de reset) pour que l'utilisateur puisse retenter
        const errMsg = result.error || 'Erreur inconnue';
        console.error('[addStop] Echec API:', errMsg);
        toast.error(`Impossible d'enregistrer l'arret : ${errMsg}. Vos donnees sont conservees, reessayez.`, {
          duration: 6000
        });
      }
    } catch (err) {
      // Erreur reseau ou timeout : donnees preservees dans newStop + localStorage
      console.error('[addStop] Exception:', err);
      toast.error('Erreur reseau. Vos donnees sont conservees, reessayez lorsque vous aurez du signal.', {
        duration: 6000
      });
    } finally {
      // TOUJOURS liberer le lock, meme en cas d'erreur
      setSavingStop(false);
    }
  };

  const updateStop = async (stopId, updates) => {
    try {
      const result = await patch(`/daily-reports/${report.id}/stops/${stopId}`, updates);
      if (result.success) {
        const updatedReport = result.data;
        setEditingStop(null);

        // Auto-sync report end_time based on the latest stop time
        const latestStopTime = getLatestStopTime(updatedReport.stops || []);
        if (latestStopTime && updatedReport.start_time) {
          const reportEndDate = updatedReport.end_date || updatedReport.date;
          const currentEnd = updatedReport.end_time ? new Date(`${reportEndDate}T${updatedReport.end_time}`) : null;
          
          if (!currentEnd || latestStopTime > currentEnd) {
            const newEndTime = `${String(latestStopTime.getHours()).padStart(2,'0')}:${String(latestStopTime.getMinutes()).padStart(2,'0')}`;
            const newEndDate = latestStopTime.toISOString().split('T')[0];
            const endPatch = { end_time: newEndTime };
            if (newEndDate !== updatedReport.date) endPatch.end_date = newEndDate;
            
            const syncResult = await patch(`/daily-reports/${updatedReport.id}`, endPatch);
            if (syncResult.success) {
              setReport(syncResult.data);
              toast.info(`Heure de fin mise à jour: ${newEndTime}`);
            } else {
              setReport(updatedReport);
            }
          } else {
            setReport(updatedReport);
          }
        } else {
          setReport(updatedReport);
        }

        // Sync status to dispatch if this stop is linked to a dispatch stop
        const updatedStop = (updatedReport.stops || []).find(s => s.id === stopId);
        if (updatedStop?.dispatch_stop_id) {
          try {
            if (updates.arrival_datetime) {
              await patch(`/dispatch/driver/stops/${updatedStop.dispatch_stop_id}/sync-status`, {
                action: 'arrival',
                datetime: updates.arrival_datetime,
              });
            }
            if (updates.departure_datetime) {
              await patch(`/dispatch/driver/stops/${updatedStop.dispatch_stop_id}/sync-status`, {
                action: 'departure',
                datetime: updates.departure_datetime,
              });
            }
          } catch (syncErr) {
            console.error('Dispatch sync error:', syncErr);
          }
        }
      }
    } catch (err) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  // Helper: find the latest time across all stops
  const getLatestStopTime = (stops) => {
    let latest = null;
    (stops || []).forEach(stop => {
      [stop.arrival_datetime, stop.departure_datetime].forEach(dt => {
        if (dt) {
          const d = new Date(dt);
          if (!isNaN(d) && (!latest || d > latest)) latest = d;
        }
      });
    });
    return latest;
  };

  const deleteStop = async (stopId) => {
    try {
      const result = await del(`/daily-reports/${report.id}/stops/${stopId}`);
      if (result.success) {
        setReport(result.data);
        toast.success('Arrêt supprimé');
      }
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Supprime un scan/photo (document) attaché à un arrêt.
  // Utilisé par le bouton corbeille sur les vignettes de scans.
  // Une fois supprimé, on recharge le rapport pour rafraîchir la vue.
  const deleteScan = async (stopId, documentId) => {
    if (!window.confirm('Supprimer ce scan ?')) return;
    try {
      const result = await del(`/documents/${documentId}`);
      if (result.success !== false) {
        // Recharge les documents pour ce stop uniquement
        try {
          const docsRes = await get(`/daily-reports/${report.id}/stops/${stopId}/documents`);
          if (docsRes.success) {
            setLoadedDocuments(prev => ({ ...prev, [stopId]: docsRes.data || [] }));
          }
        } catch {}
        // Rafraîchir aussi le compteur scanned_documents dans le rapport
        try {
          const rep = await get(`/daily-reports/${report.id}`);
          if (rep.success && rep.data) setReport(rep.data);
        } catch {}
        toast.success('Scan supprimé');
      } else {
        toast.error('Erreur suppression scan');
      }
    } catch (err) {
      toast.error('Erreur suppression scan');
    }
  };

  // Return a stop BACK to the dispatcher (e.g. truck breakdown, accidental
  // accept, route change). Works for both dispatch-linked AND manually-added
  // stops — for the latter, a new dispatch_stop is auto-created on the
  // dispatcher's board so they can reassign it to another driver.
  const returnStopToDispatch = async (stop) => {
    if (stop.stop_type === 'pause' || stop.stop_type === 'fin_trajet') {
      toast.error("Les pauses et fins de trajet ne peuvent pas être retournées.");
      return;
    }
    const reason = window.prompt(
      "Motif du retour au répartiteur ?\n\n" +
      "Exemples : bris de camion, erreur de clic, hors zone, conflit d'horaire, " +
      "changement de route..."
    );
    if (!reason || !reason.trim()) return;
    try {
      const result = await post(`/dispatch/driver/report-stops/return`, {
        report_id: report.id,
        report_stop_id: stop.id,
        reason: reason.trim(),
      });
      if (result.success) {
        // Refresh the report to reflect the removal
        const updated = await get(`/daily-reports/${report.id}`);
        if (updated.success && updated.data) {
          setReport(updated.data);
        }
        toast.success('Arrêt retourné au répartiteur');
      }
    } catch (err) {
      toast.error(err.message || 'Erreur lors du retour');
    }
  };

  const moveStop = async (stopId, direction) => {
    const currentStop = report.stops.find(s => s.id === stopId);
    if (!currentStop) return;
    
    const newOrder = direction === 'up' ? currentStop.order - 1 : currentStop.order + 1;
    
    if (newOrder < 0 || newOrder >= report.stops.length) return;
    
    try {
      const result = await post(`/daily-reports/${report.id}/stops/${stopId}/reorder?new_order=${newOrder}`);
      if (result.success) {
        setReport(result.data);
      }
    } catch (err) {
      toast.error('Erreur lors du déplacement');
    }
  };

  const resetNewStop = () => {
    setNewStop({
      client_name: '',
      address: '',
      delivery_note_numbers: [],
      quantities: { palettes: 0, units: 0, packages: 0, weight_kg: null },
      stop_type: 'delivery',
      equipment_used: 'none',
      arrival_datetime: '',
      departure_datetime: '',
      notes: ''
    });
    setDeliveryNoteInput('');
    setPendingBolFiles([]);
    setLastBolExtraction(null);
  };

  const addDeliveryNote = () => {
    if (deliveryNoteInput.trim()) {
      setNewStop(prev => ({
        ...prev,
        delivery_note_numbers: [...prev.delivery_note_numbers, deliveryNoteInput.trim()]
      }));
      setDeliveryNoteInput('');
    }
  };

  const removeDeliveryNote = (index) => {
    setNewStop(prev => ({
      ...prev,
      delivery_note_numbers: prev.delivery_note_numbers.filter((_, i) => i !== index)
    }));
  };

  const handleDocumentScan = async (stopId) => {
    setSelectedStopId(stopId);
    setShowScanDialog(true);
  };

  // Compress image and return as Blob (not base64 — saves ~33% memory)
  const compressImage = (file, maxWidth = 1200, quality = 0.6) => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new window.Image();
      const objectUrl = URL.createObjectURL(file);
      
      img.onload = () => {
        // Free the object URL immediately after loading
        URL.revokeObjectURL(objectUrl);
        
        let w = img.width;
        let h = img.height;
        
        if (w > maxWidth) {
          h = (h * maxWidth) / w;
          w = maxWidth;
        }
        
        canvas.width = w;
        canvas.height = h;
        ctx.drawImage(img, 0, 0, w, h);
        
        // Convert to Blob (binary) instead of base64 string
        canvas.toBlob(
          (blob) => {
            // Free canvas memory
            canvas.width = 0;
            canvas.height = 0;
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to compress image'));
            }
          },
          'image/jpeg',
          quality
        );
      };
      
      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Failed to load image'));
      };
      
      img.src = objectUrl;
    });
  };

  const handleFileCapture = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    for (const file of files) {
      try {
        toast.info('Optimisation du document...');
        
        // Compress image to binary Blob (memory-efficient)
        const compressedBlob = await compressImage(file);
        
        // OFFLINE FALLBACK: queue the upload as a blob mutation
        if (!navigator.onLine) {
          const { enqueueBlob } = await import('../services/offlineDB');
          await enqueueBlob({
            blob: compressedBlob,
            filename: file.name || 'scan.jpg',
            report_id: report.id,
            stop_id: selectedStopId,
            extra_form: { enhance_mode: 'auto' },
            upload_url: `/api/daily-reports/${report.id}/stops/${selectedStopId}/documents`,
          });
          toast.success('Scan sauvegardé localement — synchronisation au retour réseau');
          continue;
        }
        
        const formData = new FormData();
        formData.append('file', compressedBlob, file.name || 'scan.jpg');
        formData.append('enhance_mode', 'auto');
        
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/daily-reports/${report.id}/stops/${selectedStopId}/documents`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: formData
          }
        );
        
        if (response.ok) {
          toast.success('Document scanné et optimisé');
          // Load only the new stop's documents instead of full report refresh
          loadDocumentsForStop(report.id, selectedStopId);
        } else if (response.status === 0 || !response.status) {
          // Network error fallback: queue
          const { enqueueBlob } = await import('../services/offlineDB');
          await enqueueBlob({
            blob: compressedBlob,
            filename: file.name || 'scan.jpg',
            report_id: report.id,
            stop_id: selectedStopId,
            extra_form: { enhance_mode: 'auto' },
            upload_url: `/api/daily-reports/${report.id}/stops/${selectedStopId}/documents`,
          });
          toast.success('Scan sauvegardé localement — synchronisation au retour réseau');
        } else {
          const err = await response.json().catch(() => ({}));
          toast.error(err.detail || 'Erreur lors de l\'upload');
        }
      } catch (err) {
        // Catch-all network error: queue as offline scan
        try {
          const { enqueueBlob } = await import('../services/offlineDB');
          const compressedBlob = await compressImage(file);
          await enqueueBlob({
            blob: compressedBlob,
            filename: file.name || 'scan.jpg',
            report_id: report.id,
            stop_id: selectedStopId,
            extra_form: { enhance_mode: 'auto' },
            upload_url: `/api/daily-reports/${report.id}/stops/${selectedStopId}/documents`,
          });
          toast.success('Scan sauvegardé localement — synchronisation au retour réseau');
        } catch {
          toast.error('Erreur lors du traitement');
        }
      }
    }
    
    setShowScanDialog(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const completeReport = async (overrideEndTime = null) => {
    try {
      // Auto-fill end_time with current time if not set anywhere
      const now = new Date();
      const currentTime = now.toTimeString().slice(0, 5); // Format HH:MM
      
      // Priorité : override (fin_trajet sync) > report.end_time existant > heure actuelle
      const endTime = overrideEndTime || report.end_time || currentTime;
      
      const updateData = { 
        status: 'completed',
        end_time: endTime
      };
      
      const result = await patch(`/daily-reports/${report.id}`, updateData);
      if (result.success) {
        setReport(result.data);
        toast.success('Rapport complete');

        // Sync all linked dispatch stops as complete ("fin de trajet")
        try {
          await post(`/dispatch/driver/report/${report.id}/sync-complete`, {});
        } catch (syncErr) {
          console.error('Dispatch sync-complete error:', syncErr);
        }
      }
    } catch (err) {
      toast.error('Erreur');
    }
  };


  const handleDeleteScan = async (docId, stopId) => {
    if (!window.confirm('Supprimer ce scan ? Cette action est irreversible.')) return;
    const r = await del(`/documents/${docId}`);
    if (r.success) {
      setLoadedDocuments(prev => ({
        ...prev,
        [stopId]: (prev[stopId] || []).filter(d => d.id !== docId)
      }));
      toast.success('Scan supprime');
    } else {
      toast.error(r.error || 'Erreur suppression');
    }
  };


  const exportPDF = async () => {
    if (!report?.id) {
      toast.error('Aucun rapport à exporter');
      return;
    }
    const t = toast.loading('Génération du PDF...');
    try {
      const token = localStorage.getItem('token');
      const res = await withTimeout(
        fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/daily-reports/${report.id}/pdf`,
          { headers: { Authorization: `Bearer ${token}` } }
        ),
        45000,
        'PDF download'
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Erreur lors de la génération du PDF');
      }
      const blob = await res.blob();
      const filename = `${report.report_number || 'rapport'}.pdf`;

      // Sur Capacitor natif (Android/iOS), window.open('_blank') ne fonctionne pas
      // dans le WebView. On écrit le PDF sur disque et on ouvre le menu Share natif.
      if (Capacitor.isNativePlatform()) {
        try {
          const base64 = await blobToBase64(blob);
          const writeRes = await Filesystem.writeFile({
            path: filename,
            data: base64,
            directory: Directory.Cache,
          });
          await Share.share({
            title: filename,
            text: `Rapport ${report.report_number || ''}`,
            url: writeRes.uri,
            dialogTitle: 'Partager ou imprimer le PDF',
          });
          toast.success('PDF prêt', { id: t });
        } catch (shareErr) {
          // Utilisateur a annulé le menu Share = pas une erreur
          if (String(shareErr?.message || '').toLowerCase().includes('cancel')) {
            toast.dismiss(t);
          } else {
            console.error('PDF share error:', shareErr);
            toast.error(`Impossible d'ouvrir le menu de partage : ${shareErr?.message || 'erreur inconnue'}`, { id: t });
          }
        }
        return;
      }

      // Web : ouvrir dans nouvel onglet ou téléchargement direct
      const url = URL.createObjectURL(blob);
      const w = window.open(url, '_blank');
      if (!w) {
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      toast.success('PDF prêt — utilisez le menu pour imprimer ou partager', { id: t });
    } catch (e) {
      toast.error(e.message || 'Échec de la génération du PDF', { id: t });
    }
  };

  // Generate report summary text for sharing (complete version)
  const generateReportSummary = () => {
    if (!report) return '';
    
    let summary = `═══════════════════════════════════════\n`;
    summary += `       RAPPORT JOURNALIER\n`;
    summary += `       ${report.report_number}\n`;
    summary += `═══════════════════════════════════════\n\n`;
    
    // Company Info
    if (companyInfo.name) {
      summary += `📋 ENTREPRISE\n`;
      summary += `   ${companyInfo.name}\n`;
      if (companyInfo.address) summary += `   ${companyInfo.address}\n`;
      if (companyInfo.phone) summary += `   Tél: ${companyInfo.phone}\n`;
      if (companyInfo.email) summary += `   Email: ${companyInfo.email}\n`;
      summary += `\n`;
    }
    
    // Driver & Date
    summary += `👤 CHAUFFEUR: ${report.driver_name}\n`;
    summary += `📅 DATE: ${report.date}\n`;
    if (report.subcontractor) {
      summary += `🏢 SOUS-TRAITANT: ${report.subcontractor}\n`;
    }
    summary += `\n`;
    
    // Hours section
    if (report.start_time && report.end_time) {
      const start = new Date(`${report.date}T${report.start_time}`);
      const end = new Date(`${report.date}T${report.end_time}`);
      const totalHours = (end - start) / (1000 * 60 * 60);
      
      // Calculate pause hours
      let pauseHours = 0;
      report.stops?.forEach(stop => {
        if ((stop.stop_type === 'pause' || stop.stop_type === 'fin_trajet') && stop.arrival_datetime && stop.departure_datetime) {
          const arrival = new Date(stop.arrival_datetime);
          const departure = new Date(stop.departure_datetime);
          pauseHours += (departure - arrival) / (1000 * 60 * 60);
        }
      });
      
      const totalR = roundToQuarterHour(totalHours);
      const pauseR = roundToQuarterHour(pauseHours);
      const payableR = roundToQuarterHour(Math.max(0, totalHours - pauseHours));
      summary += `⏰ HEURES DE TRAVAIL\n`;
      summary += `   Début: ${report.start_time}\n`;
      summary += `   Fin: ${report.end_time}\n`;
      summary += `   ─────────────────\n`;
      summary += `   Total brut: ${formatHoursDecimal(totalR)}\n`;
      summary += `   Pauses: -${formatHoursDecimal(pauseR)}\n`;
      summary += `   PAYABLE: ${formatHoursDecimal(payableR)}\n`;
      summary += `\n`;
    }
    
    // Distance
    if (report.total_km) {
      summary += `🚛 DISTANCE: ${report.total_km} km\n\n`;
    }
    
    // Stops details
    summary += `═══════════════════════════════════════\n`;
    summary += `       DÉTAIL DES ARRÊTS (${report.stops?.length || 0})\n`;
    summary += `═══════════════════════════════════════\n\n`;
    
    report.stops?.sort((a, b) => a.order - b.order).forEach((stop, index) => {
      const typeIcon = stop.stop_type === 'pickup' ? '📦' : 
                       stop.stop_type === 'pause' ? '⏸️' :
                       stop.stop_type === 'fin_trajet' ? '🏁' : '🚚';
      const typeLabel = stop.stop_type === 'pickup' ? 'CUEILLETTE' : 
                       stop.stop_type === 'pause' ? 'PAUSE' :
                       stop.stop_type === 'fin_trajet' ? 'FIN DE TRAJET' : 'LIVRAISON';
      
      summary += `───────────────────────────────────────\n`;
      summary += `${typeIcon} ARRÊT #${index + 1} - ${typeLabel}\n`;
      summary += `───────────────────────────────────────\n`;
      summary += `📍 Client: ${stop.client_name}\n`;
      summary += `📫 Adresse: ${stop.address}\n`;
      
      // Pause details
      if (stop.stop_type === 'pause' && stop.pause_type) {
        const pauseLabels = {
          'lunch': 'Dîner',
          'dinner': 'Souper', 
          'break': 'Pause café',
          'overnight': 'Nuit / Repos',
          'other': 'Autre'
        };
        summary += `⏸️ Type de pause: ${pauseLabels[stop.pause_type] || stop.pause_type}\n`;
        
        if (stop.arrival_datetime && stop.departure_datetime) {
          const arrival = new Date(stop.arrival_datetime);
          const departure = new Date(stop.departure_datetime);
          const duration = (departure - arrival) / (1000 * 60 * 60);
          summary += `   Durée: ${duration.toFixed(1)}h (non-payable)\n`;
        }
      }
      
      // Delivery notes
      if (stop.stop_type !== 'pause' && stop.stop_type !== 'fin_trajet' && stop.delivery_note_numbers?.length > 0) {
        summary += `📄 Bons de livraison: ${stop.delivery_note_numbers.join(', ')}\n`;
      }
      
      // Quantities
      if (stop.stop_type !== 'pause' && stop.stop_type !== 'fin_trajet') {
        const quantities = [];
        if (stop.quantities?.palettes > 0) quantities.push(`${stop.quantities.palettes} palettes`);
        if (stop.quantities?.units > 0) quantities.push(`${stop.quantities.units} unités`);
        if (stop.quantities?.packages > 0) quantities.push(`${stop.quantities.packages} paquets`);
        if (stop.quantities?.weight_kg) quantities.push(`${stop.quantities.weight_kg} kg`);
        if (quantities.length > 0) {
          summary += `📦 Quantités: ${quantities.join(', ')}\n`;
        }
      }
      
      // Equipment
      if (stop.stop_type !== 'pause' && stop.stop_type !== 'fin_trajet' && stop.equipment_used && stop.equipment_used !== 'none') {
        const equipLabels = { 'moffett': 'Moffett', 'transpalette': 'Transpalette' };
        summary += `🔧 Équipement: ${equipLabels[stop.equipment_used] || stop.equipment_used}\n`;
      }
      
      // Times
      if (stop.arrival_datetime) {
        const arrivalDate = new Date(stop.arrival_datetime);
        summary += `⏰ Arrivée: ${arrivalDate.toLocaleString('fr-CA')}\n`;
      }
      if (stop.departure_datetime) {
        const departureDate = new Date(stop.departure_datetime);
        summary += `⏰ Départ: ${departureDate.toLocaleString('fr-CA')}\n`;
      }
      
      // Notes
      if (stop.notes) {
        summary += `📝 Notes: ${stop.notes}\n`;
      }
      
      // Scanned documents count
      if (stop.scanned_documents?.length > 0) {
        summary += `📎 Documents scannés: ${stop.scanned_documents.length} fichier(s)\n`;
      }
      
      summary += `\n`;
    });
    
    // Summary stats
    const totalDocs = report.stops?.reduce((acc, s) => acc + (s.scanned_documents?.length || 0), 0) || 0;
    const pickups = report.stops?.filter(s => s.stop_type === 'pickup').length || 0;
    const deliveries = report.stops?.filter(s => s.stop_type === 'delivery').length || 0;
    const pauses = report.stops?.filter(s => s.stop_type === 'pause').length || 0;
    
    summary += `═══════════════════════════════════════\n`;
    summary += `       RÉSUMÉ\n`;
    summary += `═══════════════════════════════════════\n`;
    summary += `   Livraisons: ${deliveries}\n`;
    summary += `   Cueillettes: ${pickups}\n`;
    summary += `   Pauses: ${pauses}\n`;
    summary += `   Documents scannés: ${totalDocs}\n`;
    summary += `═══════════════════════════════════════\n`;
    summary += `\nRapport généré le ${new Date().toLocaleString('fr-CA')}\n`;
    
    return summary;
  };

  // Share via Email
  const shareViaEmail = () => {
    const subject = encodeURIComponent(`Rapport Journalier ${report.report_number} - ${report.date}`);
    const body = encodeURIComponent(generateReportSummary());
    
    if (shareEmail) {
      window.location.href = `mailto:${shareEmail}?subject=${subject}&body=${body}`;
    } else {
      window.location.href = `mailto:?subject=${subject}&body=${body}`;
    }
    
    toast.success('Application email ouverte');
    setShowShareDialog(false);
  };

  // Share via SMS
  const shareViaSMS = () => {
    const body = encodeURIComponent(generateReportSummary());
    
    if (sharePhone) {
      window.location.href = `sms:${sharePhone}?body=${body}`;
    } else {
      window.location.href = `sms:?body=${body}`;
    }
    
    toast.success('Application SMS ouverte');
    setShowShareDialog(false);
  };

  // Print report with documents (route through backend PDF endpoint - works in PWA)
  const printReport = async () => {
    setShowShareDialog(false);
    await exportPDF();
  };

  // Use Web Share API if available
  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Rapport ${report.report_number}`,
          text: generateReportSummary(),
        });
        toast.success('Partagé avec succès');
        setShowShareDialog(false);
      } catch (err) {
        if (err.name !== 'AbortError') {
          toast.error('Erreur lors du partage');
        }
      }
    } else {
      toast.info('Utilisez les options ci-dessous');
    }
  };

  if (loading) {
    return (
      <div className="page-wrapper p-4" data-testid="report-loading">
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-slate-800 rounded-xl p-4 animate-pulse">
              <div className="h-6 bg-slate-700 rounded w-1/3 mb-4" />
              <div className="h-4 bg-slate-700 rounded w-2/3" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="page-wrapper p-4">
        <p className="text-slate-400">Rapport non trouvé</p>
      </div>
    );
  }

  return (
    <div className="page-wrapper print:pb-0" data-testid="daily-report-page">
      {/* Header */}
      <div className="app-header print:hidden">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileText className="text-amber-500" size={28} />
            <div>
              <h1 className="text-xl text-white">{report.report_number}</h1>
              <p className="text-slate-300 text-sm font-medium">{formatShortDate(report.date)}{report.end_date && report.end_date !== report.date ? ` → ${formatShortDate(report.end_date)}` : ''}</p>
            </div>
          </div>
          <StatusBadge status={report.status} />
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* === SECTION 1: EN-TÊTE === */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 print:border-black print:bg-white" data-testid="report-header">
          {/* Company Info */}
          <div className="flex items-start gap-4 mb-4 pb-4 border-b border-slate-800 print:border-black">
            <Dialog open={showCompanyDialog} onOpenChange={setShowCompanyDialog}>
              <DialogTrigger asChild>
                <button 
                  className="w-24 h-24 bg-slate-800 rounded-lg flex items-center justify-center border-2 border-dashed border-slate-700 hover:border-amber-500 transition-colors print:border-solid print:border-black"
                  data-testid="company-logo-btn"
                >
                  {companyInfo.logo_base64 ? (
                    <img 
                      src={companyInfo.logo_base64} 
                      alt="Logo" 
                      className="w-full h-full object-contain rounded-lg"
                    />
                  ) : (
                    <Building2 className="text-slate-500" size={32} />
                  )}
                </button>
              </DialogTrigger>
              <DialogContent className="bg-slate-900 border-slate-800 max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-white">Informations Entreprise</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div>
                    <Label className="label-uppercase">Logo</Label>
                    <div className="flex items-center gap-3 mt-2">
                      {companyInfo.logo_base64 && (
                        <img src={companyInfo.logo_base64} alt="Logo" className="w-16 h-16 object-contain rounded" />
                      )}
                      <Button 
                        variant="outline" 
                        onClick={() => logoInputRef.current?.click()}
                        className="border-slate-700"
                      >
                        <Image size={16} className="mr-2" />
                        Changer le logo
                      </Button>
                      <input
                        ref={logoInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="hidden"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="label-uppercase">Nom de l'entreprise</Label>
                    <Input
                      value={companyInfo.name || ''}
                      onChange={(e) => setCompanyInfo(prev => ({ ...prev, name: e.target.value }))}
                      className="mt-2 bg-slate-950 border-slate-800"
                      placeholder="Transport ABC Inc."
                    />
                  </div>
                  <div>
                    <Label className="label-uppercase">Adresse</Label>
                    <Textarea
                      value={companyInfo.address || ''}
                      onChange={(e) => setCompanyInfo(prev => ({ ...prev, address: e.target.value }))}
                      className="mt-2 bg-slate-950 border-slate-800"
                      placeholder="123 Rue Transport, Montréal QC"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="label-uppercase">Téléphone</Label>
                      <Input
                        value={companyInfo.phone || ''}
                        onChange={(e) => setCompanyInfo(prev => ({ ...prev, phone: e.target.value }))}
                        className="mt-2 bg-slate-950 border-slate-800"
                        placeholder="514-555-1234"
                      />
                    </div>
                    <div>
                      <Label className="label-uppercase">Email</Label>
                      <Input
                        value={companyInfo.email || ''}
                        onChange={(e) => setCompanyInfo(prev => ({ ...prev, email: e.target.value }))}
                        className="mt-2 bg-slate-950 border-slate-800"
                        placeholder="info@transport.com"
                      />
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={saveCompanySettings} className="btn-primary-glow">
                    <Save size={16} className="mr-2" />
                    Sauvegarder
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            
            <div className="flex-1 print:text-black">
              <p className="text-lg font-bold text-white print:text-black">{companyInfo.name || 'Nom entreprise'}</p>
              <p className="text-sm text-slate-400 print:text-black whitespace-pre-line">{companyInfo.address || 'Adresse'}</p>
              <p className="text-sm text-slate-400 print:text-black">{companyInfo.phone} {companyInfo.email && `• ${companyInfo.email}`}</p>
            </div>
          </div>

          {/* Sous-traitant */}
          <div className="mb-4">
            <Label className="label-uppercase">Sous-traitant</Label>
            <Input
              value={report.subcontractor || ''}
              onChange={(e) => setReport(prev => ({ ...prev, subcontractor: e.target.value }))}
              className="mt-2 h-12 bg-slate-950 border-slate-800 print:bg-white print:border-black"
              placeholder="Nom du sous-traitant (si applicable)"
              data-testid="subcontractor-input"
            />
          </div>

          {/* Driver & Date */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <Label className="label-uppercase">Chauffeur</Label>
              <div className="mt-2 h-12 bg-slate-800 border border-slate-700 rounded-md px-4 flex items-center text-white print:bg-white print:border-black print:text-black">
                {report.driver_name}
              </div>
            </div>
            <div>
              <Label className="label-uppercase">Date</Label>
              <Input
                type="date"
                value={report.date || ''}
                onChange={(e) => {
                  const newDate = e.target.value;
                  setReport(prev => {
                    // If end_date exists and is now BEFORE the new start date, clear it
                    const next = { ...prev, date: newDate };
                    if (prev.end_date && prev.end_date < newDate) {
                      next.end_date = '';
                    }
                    return next;
                  });
                }}
                className="mt-2 h-12 bg-slate-950 border-slate-800 print:bg-white print:border-black"
                data-testid="report-date-input"
              />
            </div>
          </div>

          {/* Times */}
          <div className="grid grid-cols-2 gap-3 mb-2">
            <div>
              <Label className="label-uppercase">Heure début</Label>
              <Input
                type="time"
                value={report.start_time || ''}
                onChange={(e) => setReport(prev => ({ ...prev, start_time: e.target.value }))}
                className="mt-2 h-12 bg-slate-950 border-slate-800 print:bg-white print:border-black"
                data-testid="start-time-input"
              />
            </div>
            <div>
              <Label className="label-uppercase">Heure fin</Label>
              <Input
                type="time"
                value={report.end_time || ''}
                onChange={(e) => setReport(prev => ({ ...prev, end_time: e.target.value }))}
                className="mt-2 h-12 bg-slate-950 border-slate-800 print:bg-white print:border-black"
                data-testid="end-time-input"
              />
            </div>
          </div>
          
          {/* End date for multi-day trips */}
          <div className="mb-4">
            <Label className="label-uppercase">Date de fin (si voyage multi-jours)</Label>
            <Input
              type="date"
              value={report.end_date || ''}
              onChange={(e) => setReport(prev => ({ ...prev, end_date: e.target.value }))}
              className="mt-2 h-12 bg-slate-950 border-slate-800 print:bg-white print:border-black"
              data-testid="end-date-input"
              min={report.date}
            />
            <p className="text-xs text-slate-500 mt-1">
              Laissez vide si le trajet se termine le même jour ({report.date})
            </p>
          </div>

          {/* Hours Summary - calculates in real-time */}
          {(() => {
            let totalHours = 0;
            let pauseHours = 0;
            
            if (report.start_time) {
              const startDate = report.date;
              const endDate = report.end_date || report.date;
              const start = new Date(`${startDate}T${report.start_time}`);
              const end = report.end_time 
                ? new Date(`${endDate}T${report.end_time}`)
                : new Date(); // Use current time if trip is still in progress
              totalHours = Math.max(0, (end - start) / (1000 * 60 * 60));
            }
            
            // Calculate pause hours from stops
            report.stops?.forEach(stop => {
              if ((stop.stop_type === 'pause' || stop.stop_type === 'fin_trajet') && stop.arrival_datetime && stop.departure_datetime) {
                const arrival = new Date(stop.arrival_datetime);
                const departure = new Date(stop.departure_datetime);
                pauseHours += (departure - arrival) / (1000 * 60 * 60);
              }
            });
            
            const payableHours = roundToQuarterHour(Math.max(0, totalHours - pauseHours));
            const totalHoursR = roundToQuarterHour(totalHours);
            const pauseHoursR = roundToQuarterHour(pauseHours);
            
            return (
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div>
                  <Label className="label-uppercase">Total brut</Label>
                  <div className="mt-2 h-12 bg-slate-800 border border-slate-700 rounded-md px-4 flex items-center text-white font-bold print:bg-white print:border-black print:text-black" title={formatHoursHuman(totalHoursR)}>
                    {totalHours > 0 ? formatHoursDecimal(totalHoursR) : '--'}
                  </div>
                </div>
                <div>
                  <Label className="label-uppercase">Pauses</Label>
                  <div className="mt-2 h-12 bg-slate-800 border border-slate-700 rounded-md px-4 flex items-center text-red-400 font-bold print:bg-white print:border-black print:text-black" title={formatHoursHuman(pauseHoursR)}>
                    {pauseHours > 0 ? `-${formatHoursDecimal(pauseHoursR)}` : '0h'}
                  </div>
                </div>
                <div>
                  <Label className="label-uppercase">Payable</Label>
                  <div className="mt-2 h-12 bg-amber-500/20 border border-amber-500/50 rounded-md px-4 flex items-center text-amber-500 font-bold print:bg-white print:border-black print:text-black" data-testid="payable-hours" title={formatHoursHuman(payableHours)}>
                    {totalHours > 0 ? formatHoursDecimal(payableHours) : '--'}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Kilometers - simplified to just total */}
          <div>
            <Label className="label-uppercase">Distance parcourue</Label>
            <div className="mt-2 h-14 bg-amber-500/20 border border-amber-500/50 rounded-md px-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Navigation className="text-amber-500" size={24} />
                <span className="text-2xl font-bold text-amber-500" data-testid="total-km">
                  {report.total_km ? `${report.total_km} km` : '-- km'}
                </span>
              </div>
              <span className="text-xs text-slate-400">
                Calculé via GPS
              </span>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              Le kilométrage est calculé automatiquement à partir du suivi GPS
            </p>
          </div>

          {/* Equipement utilise (pour facturation auto) */}
          <div className="mt-4 pt-4 border-t border-slate-800 print:border-black">
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Équipement utilisé</h3>
            <div className="grid grid-cols-3 gap-2" data-testid="equipment-toggles">
              {[
                { key: 'truck', label: 'Camion', locked: true },
                { key: 'trailer', label: 'Remorque(s)' },
                { key: 'moffett', label: 'Moffett' },
              ].map(({ key, label, locked }) => {
                const active = (report.equipment_used_report || { truck: true })[key];
                return (
                  <button
                    key={key}
                    disabled={locked || report.status === 'completed'}
                    onClick={async () => {
                      if (locked) return;
                      const newEquipment = { ...(report.equipment_used_report || { truck: true }), [key]: !active };
                      setReport(r => ({ ...r, equipment_used_report: newEquipment }));
                      try {
                        await patch(`/daily-reports/${report.id}`, { equipment_used_report: newEquipment });
                      } catch (e) { /* silent */ }
                    }}
                    className={`px-3 py-2 rounded-lg text-xs font-medium border transition-colors ${
                      active
                        ? 'bg-amber-500/15 border-amber-500/50 text-amber-300'
                        : 'bg-slate-800 border-slate-700 text-slate-500 hover:border-slate-600'
                    } ${locked ? 'opacity-100 cursor-default' : ''} ${report.status === 'completed' ? 'opacity-60 cursor-not-allowed' : ''}`}
                    data-testid={`equipment-toggle-${key}`}
                  >
                    <span className="mr-1">{active ? '☑' : '☐'}</span>
                    {label}
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-slate-500 mt-2">
              Utilisé pour sélectionner automatiquement le bon tarif à la facturation
            </p>
          </div>
        </div>

        {/* === SECTION 2: ARRÊTS === */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 print:border-black print:bg-white" data-testid="report-stops">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-white print:text-black uppercase tracking-wider">
              Arrêts ({report.stops?.length || 0})
            </h2>
            <Button
              onClick={() => {
                resetNewStop();
                setShowStopDialog(true);
              }}
              className="btn-primary-glow print:hidden"
              data-testid="add-stop-btn"
            >
              <Plus size={20} className="mr-2" />
              Ajouter arrêt
            </Button>
          </div>

          {/* Stops List */}
          {report.stops?.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <MapPin className="mx-auto mb-2 opacity-50" size={40} />
              <p>Aucun arrêt enregistré</p>
              <p className="text-sm">Cliquez sur "Ajouter arrêt" pour commencer</p>
            </div>
          ) : (
            <div className="space-y-3">
              {report.stops?.sort((a, b) => a.order - b.order).map((stop, index) => (
                <StopCard
                  key={stop.id}
                  stop={stop}
                  index={index}
                  totalStops={report.stops.length}
                  onMoveUp={() => moveStop(stop.id, 'up')}
                  onMoveDown={() => moveStop(stop.id, 'down')}
                  onEdit={() => setEditingStop(stop)}
                  onDelete={() => deleteStop(stop.id)}
                  onScan={() => handleDocumentScan(stop.id)}
                  onSign={() => setSignatureModalStop(stop)}
                  onDeleteScan={(docId) => deleteScan(stop.id, docId)}
                  onUpdate={(updates) => updateStop(stop.id, updates)}
                  isEditing={editingStop?.id === stop.id}
                  onCancelEdit={() => setEditingStop(null)}
                  getCurrentLocation={getCurrentLocation}
                  getCurrentDateTimeFormatted={getCurrentDateTimeFormatted}
                  loadedDocuments={stop.document_details || loadedDocuments[stop.id] || []}
                onDeleteDoc={handleDeleteScan}
                />
              ))}
            </div>
          )}
        </div>

        {/* Action Buttons - masqués pendant l'édition d'un arrêt pour éviter les clics accidentels sur mobile */}
        {!editingStop && (
        <div className="flex flex-col gap-3 print:hidden">
          {/* Bouton unique "Fin de journée" : crée fin_trajet + set end_time + termine le rapport en une seule action.
              Élimine les 3 boutons dangereusement proches (Fin trajet / Terminer rapport / Sauvegarder)
              qui causaient les rapports tronqués. */}
          {report.status === 'draft' && (
            <div className="sticky bottom-0 bg-slate-950 pt-3 pb-2 -mx-4 px-4 border-t border-slate-800 safe-area-bottom">
              <Button
                disabled={endingDay}
                onClick={async () => {
                  if (endingDay) return;
                  if (!window.confirm("Terminer ce voyage ?\n\nCette action va :\n1. Créer l'arrêt Fin de trajet\n2. Enregistrer l'heure de fin\n3. Fermer le rapport\n\nContinuer ?")) return;
                  setEndingDay(true);
                  const loadingToast = toast.loading('Fin de trajet en cours...');
                  const currentTime = getCurrentTimeFormatted();
                  const currentDateTime = getCurrentDateTimeFormatted();
                  try {
                    let finalEndTime = null;
                    // 1. Créer le stop fin_trajet s'il n'existe pas encore
                    //    Le backend va automatiquement synchroniser report.end_time depuis fin_trajet
                    if (!report.stops?.some(s => s.stop_type === 'fin_trajet')) {
                      let address = '';
                      try {
                        // Timeout GPS 8s max — si trop lent, on ferme quand même sans adresse
                        const loc = await withTimeout(getCurrentLocation(), 8000, 'GPS');
                        address = loc.address || '';
                      } catch (locErr) {
                        console.warn('[EndTrip] GPS ignoré:', locErr?.message);
                      }
                      const endStop = {
                        client_name: 'Fin de trajet',
                        address,
                        delivery_note_numbers: [],
                        quantities: { palettes: 0, units: 0, packages: 0, weight_kg: null },
                        stop_type: 'fin_trajet',
                        equipment_used: 'none',
                        arrival_datetime: currentDateTime,
                        departure_datetime: currentDateTime,
                        notes: 'Arret cree automatiquement a la fin du trajet',
                      };
                      // Timeout POST 20s — si le réseau stall, on remonte l'erreur
                      const stopResult = await withTimeout(
                        post(`/daily-reports/${report.id}/stops`, endStop),
                        20000,
                        'Création stop fin_trajet'
                      );
                      if (stopResult?.success) {
                        setReport(stopResult.data);
                        finalEndTime = stopResult.data?.end_time || currentTime;
                      } else {
                        throw new Error(stopResult?.error || 'Échec création arrêt fin de trajet');
                      }
                    } else {
                      finalEndTime = report.end_time || currentTime;
                    }
                    // 2. Terminer le rapport en passant explicitement la vraie fin
                    await withTimeout(completeReport(finalEndTime), 20000, 'Complétion rapport');
                    toast.success('Rapport terminé ✅', { id: loadingToast });
                  } catch (err) {
                    console.error('[EndTrip] error:', err);
                    toast.error(err?.message || 'Erreur lors de la fin du voyage', { id: loadingToast });
                  } finally {
                    setEndingDay(false);
                  }
                }}
                className="w-full h-16 bg-red-600 hover:bg-red-700 text-white font-bold text-lg shadow-lg disabled:opacity-60 disabled:cursor-wait"
                data-testid="end-day-btn"
              >
                <Check size={22} className="mr-2" />
                {endingDay ? 'Fermeture en cours…' : '🏁 Fin de trajet / Rapport'}
              </Button>
              <p className="text-[10px] text-slate-500 text-center mt-1">
                Enregistre l'arrêt Fin de trajet + ferme le rapport de ce voyage
              </p>
            </div>
          )}
          
          <div className="grid grid-cols-3 gap-3">
            <Button
              onClick={() => navigate('/reports')}
              variant="outline"
              className="h-14 border-slate-700"
              data-testid="back-to-reports-list-btn"
            >
              <ArrowLeft size={20} className="mr-2" />
              Liste
            </Button>
            <Button
              onClick={() => setShowPrintPreview(true)}
              variant="outline"
              className="h-14 border-slate-700"
              data-testid="print-preview-btn"
            >
              <Eye size={20} className="mr-2" />
              Aperçu
            </Button>
            <Button
              onClick={exportPDF}
              variant="outline"
              className="h-14 border-slate-700"
              data-testid="export-pdf-btn"
            >
              <Download size={20} className="mr-2" />
              Imprimer
            </Button>
          </div>
          
          {/* Share Button - Only visible when report is completed */}
          {report.status === 'completed' && (
            <Button
              onClick={handleOpenShareDialog}
              className="w-full h-16 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white text-lg font-bold"
              data-testid="share-btn"
            >
              <Share2 size={24} className="mr-3" />
              PARTAGER LE RAPPORT
            </Button>
          )}

          {/* New Report Button - Only visible when report is completed */}
          {report.status === 'completed' && (
            <Button
              onClick={() => navigate('/reports/new')}
              className="w-full h-14 bg-amber-600 hover:bg-amber-700 text-white text-lg font-bold"
              data-testid="new-report-btn"
            >
              <Plus size={24} className="mr-3" />
              NOUVEAU RAPPORT
            </Button>
          )}
          
          <p className="text-center text-xs text-slate-500">
            {autoSaveEnabled ? 'Sauvegarde automatique activée' : 'Sauvegarde manuelle'}
          </p>
        </div>
        )}
      </div>

      {/* Share Dialog - Hidden from print */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-md print:hidden">
          <DialogHeader>
            <DialogTitle className="text-white text-xl flex items-center gap-2">
              <Share2 className="text-amber-500" size={24} />
              Partager le rapport
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="bg-slate-800/50 rounded-lg p-3 text-center">
              <p className="text-amber-500 font-bold text-lg">{report?.report_number}</p>
              <p className="text-slate-400 text-sm">{report?.date} • {report?.stops?.length || 0} arrêt(s)</p>
              <p className="text-slate-500 text-xs mt-1">
                {report?.stops?.reduce((acc, s) => acc + (s.scanned_documents?.length || 0), 0) || 0} document(s) scanné(s)
                {loadingDocs && (
                  <span className="ml-2 inline-flex items-center gap-1">
                    <span className="w-3 h-3 border-2 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" />
                    Chargement...
                  </span>
                )}
              </p>
            </div>
            
            {/* Info banner about complete report */}
            <div className="bg-green-900/30 border border-green-700/50 rounded-lg p-3">
              <p className="text-green-400 text-sm flex items-start gap-2">
                <Check size={16} className="flex-shrink-0 mt-0.5" />
                <span>Le rapport partagé inclut: informations entreprise, heures de travail, détails complets de tous les arrêts, quantités, équipements et notes.</span>
              </p>
            </div>

            {/* Native Share (if supported) */}
            {typeof navigator !== 'undefined' && navigator.share && (
              <Button
                onClick={shareNative}
                className="w-full h-14 bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700"
              >
                <Share2 size={20} className="mr-2" />
                Partager...
              </Button>
            )}

            {/* Email Option */}
            <div className="space-y-2">
              <Label className="label-uppercase">Envoyer par Email</Label>
              <div className="flex gap-2">
                <Input
                  type="email"
                  value={shareEmail}
                  onChange={(e) => setShareEmail(e.target.value)}
                  placeholder="email@exemple.com (optionnel)"
                  className="flex-1 h-12 bg-slate-950 border-slate-800"
                />
                <Button
                  onClick={shareViaEmail}
                  className="h-12 px-6 bg-blue-600 hover:bg-blue-700"
                  data-testid="share-email-btn"
                >
                  <Mail size={20} />
                </Button>
              </div>
            </div>

            {/* SMS Option */}
            <div className="space-y-2">
              <Label className="label-uppercase">Envoyer par Texto</Label>
              <div className="flex gap-2">
                <Input
                  type="tel"
                  value={sharePhone}
                  onChange={(e) => setSharePhone(e.target.value)}
                  placeholder="+1 514 555 1234 (optionnel)"
                  className="flex-1 h-12 bg-slate-950 border-slate-800"
                />
                <Button
                  onClick={shareViaSMS}
                  className="h-12 px-6 bg-green-600 hover:bg-green-700"
                  data-testid="share-sms-btn"
                >
                  <MessageSquare size={20} />
                </Button>
              </div>
            </div>

            {/* Print Option */}
            <div className="pt-2 border-t border-slate-800">
              <Button
                onClick={printReport}
                variant="outline"
                className="w-full h-14 border-slate-700"
                data-testid="share-print-btn"
              >
                <Printer size={20} className="mr-2" />
                Imprimer le rapport
              </Button>
              <p className="text-xs text-slate-500 text-center mt-2">
                Inclut tous les détails et documents scannés
              </p>
            </div>
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" className="border-slate-700">Fermer</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Print Preview Modal */}
      <PrintPreviewModal
        open={showPrintPreview}
        onOpenChange={setShowPrintPreview}
        report={report}
        companyInfo={companyInfo}
        loadedDocuments={loadedDocuments}
        onPrint={exportPDF}
      />

      {/* Signature client - 1 sig pour tous les BOL d'un arret livraison */}
      {signatureModalStop && (
        <SignaturePad
          stopId={signatureModalStop.id}
          reportId={report?.id}
          bolCount={(loadedDocuments[signatureModalStop.id] || []).length}
          existing={(loadedDocuments[signatureModalStop.id] || []).find(d => d.signature)?.signature || null}
          onClose={() => setSignatureModalStop(null)}
          onSigned={async () => {
            // Refresh documents for this stop to display signature badge
            try {
              const result = await get(`/daily-reports/${report.id}/stops/${signatureModalStop.id}/documents`);
              if (result.success) {
                setLoadedDocuments(prev => ({ ...prev, [signatureModalStop.id]: result.data }));
              }
            } catch (e) {}
          }}
        />
      )}

      {/* Add Stop Dialog - Hidden from print */}
      <Dialog open={showStopDialog} onOpenChange={(open) => {
        // Empeche la fermeture si un enregistrement est en cours (evite perte de donnees)
        if (savingStop && !open) return;
        setShowStopDialog(open);
      }}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-lg max-h-[90vh] overflow-y-auto print:hidden">
          <DialogHeader>
            <DialogTitle className="text-white">Nouvel Arrêt</DialogTitle>
          </DialogHeader>
          <StopForm
            stop={newStop}
            setStop={setNewStop}
            deliveryNoteInput={deliveryNoteInput}
            setDeliveryNoteInput={setDeliveryNoteInput}
            addDeliveryNote={addDeliveryNote}
            removeDeliveryNote={removeDeliveryNote}
            getCurrentLocation={getCurrentLocation}
            getCurrentDateTimeFormatted={getCurrentDateTimeFormatted}
            pendingBolFiles={pendingBolFiles}
            setPendingBolFiles={setPendingBolFiles}
            setLastBolExtraction={setLastBolExtraction}
          />
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" className="border-slate-700" disabled={savingStop}>Annuler</Button>
            </DialogClose>
            <Button 
              onClick={() => addStop()} 
              className="btn-primary-glow"
              disabled={savingStop}
              data-testid="add-stop-save-button"
            >
              {savingStop ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Enregistrement...
                </>
              ) : (
                <>
                  <Plus size={16} className="mr-2" />
                  Ajouter
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Scan Dialog - Hidden from print */}
      <Dialog open={showScanDialog} onOpenChange={setShowScanDialog}>
        <DialogContent className="bg-slate-900 border-slate-800 print:hidden">
          <DialogHeader>
            <DialogTitle className="text-white">Scanner un document</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-slate-400 text-sm">
              Prenez une photo du bon de livraison ou sélectionnez une image existante.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <Button
                onClick={() => {
                  fileInputRef.current?.setAttribute('capture', 'environment');
                  fileInputRef.current?.click();
                }}
                className="h-20 flex-col bg-slate-800 hover:bg-slate-700"
              >
                <Camera size={28} className="mb-2" />
                Prendre photo
              </Button>
              <Button
                onClick={() => {
                  fileInputRef.current?.removeAttribute('capture');
                  fileInputRef.current?.click();
                }}
                variant="outline"
                className="h-20 flex-col border-slate-700"
              >
                <FileImage size={28} className="mb-2" />
                Galerie
              </Button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileCapture}
              className="hidden"
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* ========== PRINT-ONLY SECTION: Scanned Documents - FULL PAGE ========== */}
      <div className="scanned-docs-print-section" style={{ display: 'none' }}>
        {report?.stops?.sort((a, b) => a.order - b.order).map((stop) => {
          const docs = stop.document_details || loadedDocuments[stop.id] || [];
          if (docs.length === 0) return null;
          
          return docs.map((doc) => {
            const rawUrl = doc.image_url || doc.image_base64 || '';
            const imgSrc = rawUrl.startsWith('data:') ? rawUrl : (rawUrl ? `${process.env.REACT_APP_BACKEND_URL}${rawUrl}` : '');
            return (
            <div 
              key={doc.id} 
              className="document-full-page"
              style={{ 
                pageBreakBefore: 'always',
                pageBreakAfter: 'always',
                pageBreakInside: 'avoid',
                width: '100%',
                height: '100vh',
                margin: 0,
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'white'
              }}
            >
              <img
                src={imgSrc}
                alt={doc.filename || 'Document scanné'}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  margin: 0,
                  padding: 0
                }}
              />
            </div>
          );
          });
        })}
      </div>
    </div>
  );
};

// Stop Card Component
const StopCard = ({ 
  stop, 
  index, 
  totalStops, 
  onMoveUp, 
  onMoveDown, 
  onEdit, 
  onDelete, 
  onScan,
  onSign,
  onDeleteScan,
  onUpdate,
  isEditing,
  onCancelEdit,
  getCurrentLocation,
  getCurrentDateTimeFormatted,
  loadedDocuments,
  onDeleteDoc
}) => {
  const [localStop, setLocalStop] = useState(stop);
  const [deliveryNoteInput, setDeliveryNoteInput] = useState('');

  useEffect(() => {
    setLocalStop(stop);
  }, [stop]);

  const addDeliveryNote = () => {
    if (deliveryNoteInput.trim()) {
      const newNotes = [...(localStop.delivery_note_numbers || []), deliveryNoteInput.trim()];
      setLocalStop(prev => ({ ...prev, delivery_note_numbers: newNotes }));
      setDeliveryNoteInput('');
    }
  };

  const removeDeliveryNote = (idx) => {
    setLocalStop(prev => ({
      ...prev,
      delivery_note_numbers: prev.delivery_note_numbers.filter((_, i) => i !== idx)
    }));
  };

  const handleSave = () => {
    onUpdate(localStop);
  };

  if (isEditing) {
    return (
      <div className="bg-slate-800 border border-amber-500 rounded-xl p-4">
        <StopForm
          stop={localStop}
          setStop={setLocalStop}
          deliveryNoteInput={deliveryNoteInput}
          setDeliveryNoteInput={setDeliveryNoteInput}
          addDeliveryNote={addDeliveryNote}
          removeDeliveryNote={removeDeliveryNote}
          getCurrentLocation={getCurrentLocation}
          getCurrentDateTimeFormatted={getCurrentDateTimeFormatted}
        />
        <div className="flex gap-2 mt-4">
          <Button onClick={handleSave} className="flex-1 btn-primary-glow">
            <Check size={16} className="mr-2" />
            Sauvegarder
          </Button>
          <Button onClick={onCancelEdit} variant="outline" className="border-slate-700">
            <X size={16} />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="bg-slate-800 border border-slate-700 rounded-xl p-4 print:border-black print:bg-white"
      data-testid={`stop-card-${stop.id}`}
    >
      <div className="flex items-start gap-3">
        {/* Order number & Move buttons */}
        <div className="flex flex-col items-center gap-1 print:hidden">
          <span className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center text-black font-bold text-sm">
            {index + 1}
          </span>
          <button
            onClick={onMoveUp}
            disabled={index === 0}
            className="w-8 h-8 rounded bg-slate-700 flex items-center justify-center disabled:opacity-30"
            data-testid={`move-up-${stop.id}`}
          >
            <ChevronUp size={16} className="text-white" />
          </button>
          <button
            onClick={onMoveDown}
            disabled={index === totalStops - 1}
            className="w-8 h-8 rounded bg-slate-700 flex items-center justify-center disabled:opacity-30"
            data-testid={`move-down-${stop.id}`}
          >
            <ChevronDown size={16} className="text-white" />
          </button>
        </div>
        
        <div className="print:block hidden">
          <span className="w-8 h-8 rounded-full bg-black flex items-center justify-center text-white font-bold text-sm">
            {index + 1}
          </span>
        </div>

        {/* Stop Details */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <h3 className="text-lg font-bold text-white print:text-black truncate">{stop.client_name}</h3>
            <StatusBadge 
              status={stop.stop_type} 
              customLabel={
                stop.stop_type === 'pickup' ? 'Cueillette' : 
                stop.stop_type === 'pause' ? 'Pause' : 
                stop.stop_type === 'fin_trajet' ? 'Fin de trajet' :
                'Livraison'
              } 
            />
            {(stop.stop_type === 'livraison' || stop.stop_type === 'delivery') && loadedDocuments?.some?.(d => d.signature_id) && (
              <span
                className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 flex items-center gap-1"
                data-testid={`signed-badge-${stop.id}`}
                title={`Signé par ${loadedDocuments.find(d => d.signature)?.signature?.signed_by_name || ''}`}
              >
                <Check size={10} /> Signé
              </span>
            )}
          </div>
          
          {/* Pause type indicator */}
          {stop.stop_type === 'pause' && stop.pause_type && (
            <p className="text-amber-400 text-sm mb-2 font-medium">
              ⏸ {
                stop.pause_type === 'lunch' ? 'Dîner' :
                stop.pause_type === 'dinner' ? 'Souper' :
                stop.pause_type === 'break' ? 'Pause café' :
                stop.pause_type === 'overnight' ? 'Nuit / Repos' :
                'Autre pause'
              }
              {stop.arrival_datetime && stop.departure_datetime && (() => {
                const arrival = new Date(stop.arrival_datetime);
                const departure = new Date(stop.departure_datetime);
                const duration = (departure - arrival) / (1000 * 60 * 60);
                return ` (${duration.toFixed(1)}h non-payable)`;
              })()}
            </p>
          )}
          
          {stop.stop_type !== 'pause' && stop.stop_type !== 'fin_trajet' && (
          <p className="text-slate-400 print:text-black text-sm mb-2">
            <MapPin size={14} className="inline mr-1" />
            {stop.address}
          </p>
          )}
          
          {stop.stop_type === 'pause' && stop.address && (
          <p className="text-slate-400 print:text-black text-sm mb-2">
            <MapPin size={14} className="inline mr-1" />
            {stop.address}
          </p>
          )}
          
          {/* Delivery Notes - only for non-pause */}
          {stop.stop_type !== 'pause' && stop.stop_type !== 'fin_trajet' && stop.delivery_note_numbers?.length > 0 && (
            <div className="mb-2">
              <span className="text-xs text-slate-500 uppercase">Bons: </span>
              <span className="text-amber-400 print:text-black text-sm">
                {stop.delivery_note_numbers.join(', ')}
              </span>
            </div>
          )}
          
          {/* Quantities - only for non-pause */}
          {stop.stop_type !== 'pause' && stop.stop_type !== 'fin_trajet' && (
          <div className="flex flex-wrap gap-2 text-xs mb-2">
            {stop.quantities?.palettes > 0 && (
              <span className="bg-slate-700 px-2 py-1 rounded print:bg-gray-200">
                {stop.quantities.palettes} palettes
              </span>
            )}
            {stop.quantities?.units > 0 && (
              <span className="bg-slate-700 px-2 py-1 rounded print:bg-gray-200">
                {stop.quantities.units} unités
              </span>
            )}
            {stop.quantities?.packages > 0 && (
              <span className="bg-slate-700 px-2 py-1 rounded print:bg-gray-200">
                {stop.quantities.packages} paquets
              </span>
            )}
            {stop.quantities?.weight_kg && (
              <span className="bg-slate-700 px-2 py-1 rounded print:bg-gray-200">
                {stop.quantities.weight_kg} kg
              </span>
            )}
          </div>
          )}
          
          {/* Equipment & Times */}
          <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-sm text-slate-300 print:text-black mt-1">
            {stop.stop_type !== 'pause' && stop.stop_type !== 'fin_trajet' && stop.equipment_used && stop.equipment_used !== 'none' && (
              <span className="inline-flex items-center gap-1.5">
                <TruckIcon size={14} className="inline" />
                {stop.equipment_used === 'moffett' ? 'Moffett' : 'Transpalette'}
              </span>
            )}
            {stop.arrival_datetime && (
              <span className="inline-flex items-center gap-1.5">
                <Clock size={14} className="inline text-amber-400" />
                <span className="text-slate-400">Arrivée :</span>
                <strong className="text-white font-semibold tabular-nums">{formatStopDateTime(stop.arrival_datetime)}</strong>
              </span>
            )}
            {stop.departure_datetime && (
              <span className="inline-flex items-center gap-1.5">
                <Clock size={14} className="inline text-emerald-400" />
                <span className="text-slate-400">Départ :</span>
                <strong className="text-white font-semibold tabular-nums">{formatStopDateTime(stop.departure_datetime)}</strong>
              </span>
            )}
          </div>
          
          {/* Notes */}
          {stop.notes && (
            <p className="text-slate-500 print:text-gray-600 text-sm mt-2 italic">
              {stop.notes}
            </p>
          )}
          
          {/* Scanned Documents - with thumbnails */}
          {stop.scanned_documents?.length > 0 && (
            <div className={`mt-3 ${stop.stop_type !== 'delivery' ? 'print:hidden' : ''}`}>
              <span className="text-xs text-slate-400 mb-2 block">
                <FileImage size={12} className="inline mr-1" />
                {stop.scanned_documents.length} document(s) scanné(s)
              </span>
              {loadedDocuments && loadedDocuments.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-1">
                  {loadedDocuments.map((doc, idx) => {
                    const rawUrl = doc.thumb_url || doc.image_url || doc.image_base64 || '';
                    const imgSrc = rawUrl.startsWith('data:') ? rawUrl : (rawUrl ? `${process.env.REACT_APP_BACKEND_URL}${rawUrl}` : '');
                    const rawFull = doc.image_url || doc.image_base64 || '';
                    const fullSrc = rawFull.startsWith('data:') ? rawFull : (rawFull ? `${process.env.REACT_APP_BACKEND_URL}${rawFull}` : '');
                    return (
                      <div key={doc.id || idx} className="flex flex-col items-center gap-1">
                        <img
                          src={imgSrc}
                          alt={doc.filename || `Scan ${idx + 1}`}
                          className="w-20 h-20 object-cover rounded border border-slate-600 cursor-pointer hover:opacity-80"
                          onClick={() => {
                            window.open(fullSrc, '_blank');
                          }}
                          data-testid={`scan-thumb-${stop.id}-${idx}`}
                        />
                        {onDeleteScan && doc.id && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onDeleteScan(doc.id);
                            }}
                            className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full bg-red-600 hover:bg-red-500 text-white flex items-center justify-center shadow-md border border-red-900 print:hidden"
                            title="Supprimer ce scan"
                            data-testid={`scan-delete-${stop.id}-${idx}`}
                          >
                            <X size={14} strokeWidth={3} />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col gap-2 print:hidden">
          <Button 
            onClick={onScan} 
            size="sm" 
            variant="outline" 
            className="border-slate-700 h-10 w-10 p-0"
            data-testid={`scan-btn-${stop.id}`}
          >
            <Camera size={16} />
          </Button>
          {(stop.stop_type === 'livraison' || stop.stop_type === 'delivery') && (
            <Button
              onClick={onSign}
              size="sm"
              variant="outline"
              className={`h-10 w-10 p-0 ${
                loadedDocuments?.some?.(d => d.signature_id)
                  ? 'border-emerald-600 text-emerald-400 bg-emerald-500/10'
                  : 'border-amber-600 text-amber-400 hover:bg-amber-500/10'
              }`}
              data-testid={`sign-btn-${stop.id}`}
              title={loadedDocuments?.some?.(d => d.signature_id) ? 'Signé — re-signer' : 'Faire signer le client'}
            >
              <Pen size={16} />
            </Button>
          )}
          <Button 
            onClick={onEdit} 
            size="sm" 
            variant="outline" 
            className="border-slate-700 h-10 w-10 p-0"
            data-testid={`edit-btn-${stop.id}`}
          >
            <Edit2 size={16} />
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button 
                size="sm" 
                variant="outline" 
                className="border-red-900 text-red-500 h-10 w-10 p-0"
                data-testid={`delete-btn-${stop.id}`}
              >
                <Trash2 size={16} />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-slate-900 border-slate-800">
              <AlertDialogHeader>
                <AlertDialogTitle className="text-white">Supprimer cet arrêt?</AlertDialogTitle>
                <AlertDialogDescription className="text-slate-400">
                  Cette action est irréversible. Les documents scannés seront également supprimés.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="bg-slate-800 border-slate-700 text-white">Annuler</AlertDialogCancel>
                <AlertDialogAction onClick={onDelete} className="bg-red-600 hover:bg-red-700">
                  Supprimer
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </div>
  );
};

// Stop Form Component
const StopForm = ({ 
  stop, 
  setStop, 
  deliveryNoteInput, 
  setDeliveryNoteInput, 
  addDeliveryNote, 
  removeDeliveryNote,
  getCurrentLocation,
  getCurrentDateTimeFormatted,
  pendingBolFiles,
  setPendingBolFiles,
  setLastBolExtraction
}) => {
  const [locating, setLocating] = useState(false);
  const [scanning, setScanning] = useState(false);
  const bolInputRef = useRef(null);

  const handleLocateMe = async () => {
    setLocating(true);
    try {
      const location = await getCurrentLocation();
      setStop(prev => ({ ...prev, address: location.address }));
      toast.success('Adresse recuperee');
    } catch (err) {
      toast.error('Impossible de recuperer la position');
      console.error(err);
    } finally {
      setLocating(false);
    }
  };

  const handleScanBOL = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Safety: reject huge files that saturate upload/OCR (Android caméra HDR peut faire >15MB)
    if (file.size > 20 * 1024 * 1024) {
      toast.error(`Fichier trop volumineux (${(file.size / 1024 / 1024).toFixed(1)} Mo). Max 20 Mo.`);
      if (bolInputRef.current) bolInputRef.current.value = '';
      return;
    }
    setScanning(true);
    const scanToast = toast.loading('Analyse du BOL en cours (peut prendre 30-60s)…');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      // Timeout 90s — largement suffisant pour OpenAI Vision, empêche le hang infini
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 90_000);
      let res;
      try {
        res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/scan/bol`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData,
          signal: controller.signal,
        });
      } finally {
        clearTimeout(timeoutId);
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || err.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      if (data.success && data.extracted) {
        const ext = data.extracted;

        // Store file for later upload as document attachment
        if (setPendingBolFiles) {
          setPendingBolFiles(prev => [...prev, { blob: file, name: file.name || 'bol_scan.jpg' }]);
        }

        // Store original extraction for autolearning correction
        if (setLastBolExtraction) {
          setLastBolExtraction({
            client_name: ext.client_name || '',
            destination_name: ext.destination_name || '',
            destination_city: ext.destination_city || '',
            destination_address: ext.destination_address || '',
            city: ext.city || '',
            address: ext.address || '',
            expeditor_name: ext.expeditor_name || '',
            expeditor_city: ext.expeditor_city || '',
            bol_number: ext.bol_number || '',
            palettes: ext.palettes || 0,
            weight_kg: ext.weight_kg || 0,
          });
        }

        setStop(prev => {
          const updated = { ...prev };
          const isPickup = prev.stop_type === 'pickup';
          const isFirstScan = !prev.client_name;

          if (isPickup) {
            // Cueillette: client = expediteur
            if (isFirstScan || !prev.client_name) {
              if (ext.expeditor_name) updated.client_name = ext.expeditor_name;
              if (ext.expeditor_city) updated.city = ext.expeditor_city;
              if (ext.expeditor_address) updated.address = ext.expeditor_address;
            }
            if (ext.expeditor_name) updated.expeditor_name = ext.expeditor_name;
            if (ext.expeditor_city) updated.expeditor_city = ext.expeditor_city;
          } else {
            // Livraison: client = SOLD TO si dispo, sinon destination (SHIPPED TO)
            if (isFirstScan || !prev.client_name) {
              const name = ext.client_name || ext.destination_name;
              const city = ext.destination_city || ext.city;
              const addr = ext.destination_address || ext.address;
              if (name) updated.client_name = name;
              if (city) updated.city = city;
              if (addr) updated.address = addr;
            }
            if (ext.expeditor_name) updated.expeditor_name = ext.expeditor_name;
            if (ext.expeditor_city) updated.expeditor_city = ext.expeditor_city;
          }

          if (ext.equipment_used && ext.equipment_used !== 'none' && (!prev.equipment_used || prev.equipment_used === 'none')) {
            updated.equipment_used = ext.equipment_used;
          }

          // Quantities: ACCUMULATE across multiple scans
          const qty = { ...(prev.quantities || {}) };
          if (ext.palettes > 0) qty.palettes = (qty.palettes || 0) + ext.palettes;
          if (ext.weight_kg > 0) qty.weight_kg = (qty.weight_kg || 0) + ext.weight_kg;
          updated.quantities = qty;

          // BOL number: ADD to list
          if (ext.bol_number) {
            const existing = prev.delivery_note_numbers || [];
            if (!existing.includes(ext.bol_number)) {
              updated.delivery_note_numbers = [...existing, ext.bol_number];
            }
          }
          return updated;
        });
        const bolCount = (pendingBolFiles?.length || 0) + 1;
        toast.success(`BOL extrait (${bolCount} scan${bolCount > 1 ? 's' : ''})`, { id: scanToast });
      } else {
        toast.error(data.error || 'Extraction échouée — reprenez la photo en pleine lumière', { id: scanToast });
      }
    } catch (err) {
      console.error('[BOL scan] error:', err);
      const isAbort = err?.name === 'AbortError';
      toast.error(
        isAbort
          ? 'Le scan a pris trop de temps (90s). Réessayez avec une meilleure connexion.'
          : `Erreur scan : ${err?.message || 'inconnue'}. Réessayez ou reprenez la photo.`,
        { id: scanToast }
      );
    } finally {
      setScanning(false);
      if (bolInputRef.current) bolInputRef.current.value = '';
    }
  };

  const setArrivalNow = () => {
    setStop(prev => ({ ...prev, arrival_datetime: getCurrentDateTimeFormatted() }));
    toast.success('Heure d\'arrivée enregistrée');
  };

  const setDepartureNow = () => {
    setStop(prev => ({ ...prev, departure_datetime: getCurrentDateTimeFormatted() }));
    toast.success('Heure de départ enregistrée');
  };

  return (
    <div className="space-y-4">
      {/* Type d'arret - SELECTIONNER EN PREMIER pour que le scanner sache quoi extraire */}
      <div className="bg-slate-900/50 border border-slate-700 rounded-lg p-3">
        <Label className="label-uppercase text-emerald-400">{"1. Type d'arrêt *"}</Label>
        <p className="text-xs text-slate-400 mt-1 mb-2">
          {"Sélectionnez d'abord le type d'arrêt — le scanner adaptera l'extraction (cueillette = expéditeur, livraison = destinataire)"}
        </p>
        <NativeSelect
          value={stop.stop_type}
          onValueChange={(value) => setStop(prev => ({ ...prev, stop_type: value }))}
          className="mt-1"
          data-testid="stop-type-select"
        >
          <NativeSelectOption value="delivery">Livraison</NativeSelectOption>
          <NativeSelectOption value="pickup">Cueillette</NativeSelectOption>
          <NativeSelectOption value="pause">Pause (non-payable)</NativeSelectOption>
          <NativeSelectOption value="fin_trajet">Fin de trajet (non-payable)</NativeSelectOption>
        </NativeSelect>
        {(stop.stop_type === 'pause' || stop.stop_type === 'fin_trajet') && (
          <p className="text-xs text-amber-500 mt-2">
            {stop.stop_type === 'fin_trajet' ? '🏁 Cet arrêt n\'est pas comptabilisé dans les heures payables' : '⏸ Les pauses sont soustraites du total des heures payables'}
          </p>
        )}
      </div>

      {/* Pause type selector - only show for pause stops */}
      {stop.stop_type === 'pause' && (
        <div>
          <Label className="label-uppercase">Type de pause</Label>
          <NativeSelect
            value={stop.pause_type || 'lunch'}
            onValueChange={(value) => setStop(prev => ({ ...prev, pause_type: value }))}
            className="mt-2"
          >
            <NativeSelectOption value="lunch">Dîner</NativeSelectOption>
            <NativeSelectOption value="dinner">Souper</NativeSelectOption>
            <NativeSelectOption value="break">Pause café</NativeSelectOption>
            <NativeSelectOption value="overnight">Nuit / Repos</NativeSelectOption>
            <NativeSelectOption value="other">Autre</NativeSelectOption>
          </NativeSelect>
        </div>
      )}

      {/* Scanner BOL - Pre-fill stop from scanned document */}
      {stop.stop_type !== 'pause' && stop.stop_type !== 'fin_trajet' && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
          <Label className="label-uppercase text-amber-400">2. Scanner le BOL (optionnel)</Label>
          <label className="flex items-center justify-center gap-3 cursor-pointer w-full py-2 mt-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-black font-semibold transition-colors">
            {scanning ? (
              <>
                <span className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                Extraction en cours...
              </>
            ) : (
              <>
                <Scan size={20} />
                Scanner un bon de livraison{pendingBolFiles?.length > 0 ? ` (${pendingBolFiles.length} scanne${pendingBolFiles.length > 1 ? 's' : ''})` : ''}
              </>
            )}
            <input
              ref={bolInputRef}
              type="file"
              accept="image/*,application/pdf"
              capture="environment"
              onChange={handleScanBOL}
              className="hidden"
              disabled={scanning}
              data-testid="scan-bol-input"
            />
          </label>
          <p className="text-xs text-amber-400/70 text-center mt-2">
            {stop.stop_type === 'pickup'
              ? '📦 Cueillette : le scanner extraira l\'expéditeur comme client'
              : '🚚 Livraison : le scanner extraira le destinataire comme client'}
          </p>
        </div>
      )}

      <div>
        <Label className="label-uppercase">Nom du client / Lieu *</Label>
        <Input
          value={stop.client_name}
          onChange={(e) => setStop(prev => ({ ...prev, client_name: e.target.value }))}
          className="mt-2 h-12 bg-slate-950 border-slate-800"
          placeholder="Entreprise ABC"
        />
      </div>
      
      <div>
        <Label className="label-uppercase">Adresse complète *</Label>
        <div className="flex gap-2 mt-2">
          <Textarea
            value={stop.address}
            onChange={(e) => setStop(prev => ({ ...prev, address: e.target.value }))}
            className="flex-1 bg-slate-950 border-slate-800 min-h-[60px]"
            placeholder="123 Rue Principale, Montréal QC H1A 1A1"
          />
          <Button
            type="button"
            onClick={handleLocateMe}
            disabled={locating}
            className="h-[60px] w-[60px] bg-blue-600 hover:bg-blue-700 p-0 flex-shrink-0"
            title="Utiliser ma position actuelle"
          >
            {locating ? (
              <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <Crosshair size={24} />
            )}
          </Button>
        </div>
        <p className="text-xs text-slate-500 mt-1">
          Cliquez sur le bouton pour utiliser votre position GPS actuelle
        </p>
      </div>
      
      {/* Expeditor info (from BOL scan) */}
      {stop.stop_type !== 'pause' && stop.stop_type !== 'fin_trajet' && (stop.expeditor_name || stop.expeditor_city) && (
        <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
          <Label className="label-uppercase text-blue-400">Expediteur (du BOL)</Label>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <div>
              <Input
                value={stop.expeditor_name || ''}
                onChange={(e) => setStop(prev => ({ ...prev, expeditor_name: e.target.value }))}
                className="h-10 bg-slate-950 border-slate-800 text-sm"
                placeholder="Nom expediteur"
              />
            </div>
            <div>
              <Input
                value={stop.expeditor_city || ''}
                onChange={(e) => setStop(prev => ({ ...prev, expeditor_city: e.target.value }))}
                className="h-10 bg-slate-950 border-slate-800 text-sm"
                placeholder="Ville expediteur"
              />
            </div>
          </div>
        </div>
      )}

      {/* Contact info from dispatch - read-only display */}
      {(stop.contact_name || stop.contact_phone) && stop.stop_type !== 'pause' && stop.stop_type !== 'fin_trajet' && (
      <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
        <Label className="label-uppercase text-blue-400">Contact du client</Label>
        <div className="grid grid-cols-2 gap-2 mt-2">
          {stop.contact_name && (
            <div className="flex items-center gap-2 text-white text-sm">
              <User size={14} className="text-blue-400 shrink-0" />
              {stop.contact_name}
            </div>
          )}
          {stop.contact_phone && (
            <a href={`tel:${stop.contact_phone}`} className="flex items-center gap-2 text-blue-400 text-sm hover:underline">
              <Phone size={14} className="shrink-0" />
              {stop.contact_phone}
            </a>
          )}
        </div>
      </div>
      )}

      {/* Dispatch items (bon + quantity) - read-only display */}
      {stop.dispatch_items?.length > 0 && stop.stop_type !== 'pause' && stop.stop_type !== 'fin_trajet' && (
      <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
        <Label className="label-uppercase text-amber-400">Articles de la commande</Label>
        {stop.dispatch_items.map((item, i) => (
          <div key={i} className="flex justify-between items-center mt-1.5 text-sm">
            <span className="text-white font-mono">{item.bon_number || '-'}</span>
            <span className="text-slate-300">{item.quantity || ''}</span>
          </div>
        ))}
      </div>
      )}

      {/* Only show delivery notes for non-pause stops */}
      {stop.stop_type !== 'pause' && (
      <div>
        <Label className="label-uppercase">Numéros de bons de livraison</Label>
        <div className="flex gap-2 mt-2">
          <Input
            value={deliveryNoteInput}
            onChange={(e) => setDeliveryNoteInput(e.target.value)}
            className="h-12 bg-slate-950 border-slate-800"
            placeholder="BL-12345"
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addDeliveryNote())}
          />
          <Button onClick={addDeliveryNote} className="h-12 px-4" variant="outline">
            <Plus size={16} />
          </Button>
        </div>
        {stop.delivery_note_numbers?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {stop.delivery_note_numbers.map((note, idx) => (
              <span 
                key={idx} 
                className="bg-amber-500/20 text-amber-400 px-3 py-1 rounded-full text-sm flex items-center gap-2"
              >
                {note}
                <button onClick={() => removeDeliveryNote(idx)} className="hover:text-red-400">
                  <X size={14} />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
      )}
      
      {/* Only show quantities for non-pause stops */}
      {stop.stop_type !== 'pause' && (
      <div>
        <Label className="label-uppercase">Quantités</Label>
        <div className="grid grid-cols-2 gap-2 mt-2">
          <div>
            <Input
              type="number"
              value={stop.quantities?.palettes || ''}
              onChange={(e) => setStop(prev => ({ 
                ...prev, 
                quantities: { ...prev.quantities, palettes: parseInt(e.target.value) || 0 }
              }))}
              className="h-12 bg-slate-950 border-slate-800"
              placeholder="Palettes"
            />
          </div>
          <div>
            <Input
              type="number"
              value={stop.quantities?.units || ''}
              onChange={(e) => setStop(prev => ({ 
                ...prev, 
                quantities: { ...prev.quantities, units: parseInt(e.target.value) || 0 }
              }))}
              className="h-12 bg-slate-950 border-slate-800"
              placeholder="Unités"
            />
          </div>
          <div>
            <Input
              type="number"
              value={stop.quantities?.packages || ''}
              onChange={(e) => setStop(prev => ({ 
                ...prev, 
                quantities: { ...prev.quantities, packages: parseInt(e.target.value) || 0 }
              }))}
              className="h-12 bg-slate-950 border-slate-800"
              placeholder="Paquets"
            />
          </div>
          <div>
            <Input
              type="number"
              step="0.1"
              value={stop.quantities?.weight_kg || ''}
              onChange={(e) => setStop(prev => ({ 
                ...prev, 
                quantities: { ...prev.quantities, weight_kg: parseFloat(e.target.value) || null }
              }))}
              className="h-12 bg-slate-950 border-slate-800"
              placeholder="Poids (kg)"
            />
          </div>
        </div>
      </div>
      )}
      
      {/* Only show equipment for non-pause stops */}
      {stop.stop_type !== 'pause' && (
      <div>
        <Label className="label-uppercase">Équipement utilisé</Label>
        <NativeSelect
          value={stop.equipment_used || 'none'}
          onValueChange={(value) => setStop(prev => ({ ...prev, equipment_used: value }))}
          className="mt-2"
        >
          <NativeSelectOption value="none">Aucun</NativeSelectOption>
          <NativeSelectOption value="moffett">Moffett</NativeSelectOption>
          <NativeSelectOption value="transpalette">Transpalette</NativeSelectOption>
        </NativeSelect>
      </div>
      )}
      
      {/* Arrival/Departure with auto-fill buttons */}
      <div className="space-y-3">
        <div>
          <Label className="label-uppercase">Heure arrivée</Label>
          <div className="flex gap-2 mt-2">
            <Input
              type="datetime-local"
              value={stop.arrival_datetime || ''}
              onChange={(e) => setStop(prev => ({ ...prev, arrival_datetime: e.target.value }))}
              className="flex-1 h-12 bg-slate-950 border-slate-800"
            />
            <Button
              type="button"
              onClick={setArrivalNow}
              className="h-12 px-4 bg-green-600 hover:bg-green-700"
              title="Maintenant"
            >
              <Clock size={18} className="mr-2" />
              ARRIVÉ
            </Button>
          </div>
        </div>
        <div>
          <Label className="label-uppercase">Heure départ</Label>
          <div className="flex gap-2 mt-2">
            <Input
              type="datetime-local"
              value={stop.departure_datetime || ''}
              onChange={(e) => setStop(prev => ({ ...prev, departure_datetime: e.target.value }))}
              className="flex-1 h-12 bg-slate-950 border-slate-800"
            />
            <Button
              type="button"
              onClick={setDepartureNow}
              className="h-12 px-4 bg-red-600 hover:bg-red-700"
              title="Maintenant"
            >
              <Clock size={18} className="mr-2" />
              PARTI
            </Button>
          </div>
        </div>
      </div>
      
      <div>
        <Label className="label-uppercase">Notes / Remarques</Label>
        <Textarea
          value={stop.notes || ''}
          onChange={(e) => setStop(prev => ({ ...prev, notes: e.target.value }))}
          className="mt-2 bg-slate-950 border-slate-800"
          placeholder="Remarques additionnelles..."
        />
      </div>
    </div>
  );
};

export default DailyReportPage;
